import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import type { IInvestigationReport } from '@apis/investigationReports';
import { handlePlainTextPaste } from '../handlePlainTextPaste';
import { useEditableReportTitle } from '../hooks/useEditableReportTitle';
import classes from './ReportHeader.module.css';
import { isImeComposing } from '@utils/ime';

interface IReportHeaderProps {
  siteId: string;
  reportId: string;
  record: IInvestigationReport | null | undefined;
  defaults: { title: string; description: string };
  backHref: string;
  /** Pre-translated aria-label for the back link. */
  backLabel: string;
  /** Pre-translated aria-label for the rename button + title textbox. */
  renameLabel: string;
  /** Pre-translated aria-label for the description textbox. Falls back to
   * `defaults.description` when omitted — note that reads as content, not
   * as a field label, so callers should supply a proper label key. */
  descriptionLabel?: string;
  /** Date / range string shown in the right pill. Empty/undefined hides it. */
  dateText?: string;
  /** Optional right-side content (e.g. a download menu). Sits on the same row as the date pill. */
  rightSlot?: ReactNode;
}

/**
 * Shared editable header for analytics report pages. Owns:
 *   - the back link (top-left)
 *   - the editable title (contentEditable h1 with IME-safe keyboard plumbing)
 *   - the editable description (contentEditable p with the same plumbing)
 *   - the pencil rename button (focuses the title)
 *   - the date pill (top-right, optional)
 *   - an optional rightSlot (next to the date)
 *
 * All keyboard behaviour:
 *   - Title: Enter blurs, Escape reverts, IME composition guard suppresses both.
 *   - Description: Escape reverts, IME composition guard suppresses it.
 *   - Both: paste is sanitised through `handlePlainTextPaste`.
 *
 * The state machinery (PUT-on-blur, seed-once, language-flip protection) is
 * delegated to `useEditableReportTitle`.
 */
export function ReportHeader({
  siteId,
  reportId,
  record,
  defaults,
  backHref,
  backLabel,
  renameLabel,
  descriptionLabel,
  dateText,
  rightSlot,
}: IReportHeaderProps) {
  const {
    name,
    description,
    titleRef,
    descRef,
    handleTitleBlur,
    handleDescBlur,
    revertTitle,
    revertDesc,
  } = useEditableReportTitle({
    siteId,
    reportId,
    record,
    defaultName: defaults.title,
    defaultDescription: defaults.description,
  });

  return (
    <header className={classes.header}>
      <div className={classes.headerInner}>
        <Link to={backHref} className={classes.back} aria-label={backLabel}>
          ←
        </Link>
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <h1
              ref={titleRef}
              className={classes.title}
              contentEditable
              suppressContentEditableWarning
              spellCheck={false}
              role="textbox"
              aria-multiline={false}
              onBlur={handleTitleBlur}
              onPaste={handlePlainTextPaste}
              onKeyDown={(e) => {
                if (isImeComposing(e)) return;
                if (e.key === 'Enter') {
                  e.preventDefault();
                  titleRef.current?.blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  revertTitle();
                }
              }}
              aria-label={renameLabel}
            >
              {name}
            </h1>
            <button
              type="button"
              onClick={() => titleRef.current?.focus()}
              className={classes.renameBtn}
              aria-label={renameLabel}
            >
              <CardIcon name="edit" size={16} />
            </button>
          </div>
          <p
            ref={descRef}
            className={classes.subtitle}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            role="textbox"
            aria-multiline={true}
            onBlur={handleDescBlur}
            onPaste={handlePlainTextPaste}
            onKeyDown={(e) => {
              if (isImeComposing(e)) return;
              if (e.key === 'Escape') {
                e.preventDefault();
                revertDesc();
              }
            }}
            aria-label={descriptionLabel ?? defaults.description}
          >
            {description}
          </p>
        </div>
        {dateText ? (
          <div className={classes.dateGroup}>
            <CardIcon name="calendar" size={16} />
            <span className={classes.date}>{dateText}</span>
          </div>
        ) : null}
        {rightSlot ? <div className={classes.rightSlot}>{rightSlot}</div> : null}
      </div>
    </header>
  );
}
